import { css } from 'styled-components'

/**
 * The portfolio screen's own layout, in the board's language.
 *
 * Everything it paints with — the palette, the chrome, the chips, the mono
 * figures — comes from the Pulse stylesheet it is mounted beside. What is here
 * is only what a page of panels needs and a page of columns did not: a grid
 * that reflows at three widths, a panel frame, the stat tile, the bucket bars,
 * the heat grid and the tables.
 *
 * The ladder is driven by container queries on the same "pulse" container the
 * board declares, so the two screens agree about what counts as narrow.
 */
export const portfolioStyles = css`
  .body {
    flex-grow: 1;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: none;
    padding: 0 var(--gutter) 24px;
  }

  .body::-webkit-scrollbar {
    display: none;
  }

  .grid {
    display: grid;
    gap: 10px;
    grid-template-columns: 330px minmax(0, 1fr) 360px;
    grid-template-areas:
      'balance curve perf'
      'analysis analysis entry'
      'hours hours hours'
      'lists lists lists';
  }

  @container pulse (max-width: 1500px) {
    .grid {
      grid-template-columns: 330px minmax(0, 1fr);
      grid-template-areas:
        'balance curve'
        'perf analysis'
        'entry entry'
        'hours hours'
        'lists lists';
    }
  }

  @container pulse (max-width: 1040px) {
    .grid {
      grid-template-columns: minmax(0, 1fr);
      grid-template-areas:
        'balance'
        'curve'
        'perf'
        'analysis'
        'entry'
        'hours'
        'lists';
    }
  }

  .pn {
    position: relative;
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 9px;
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
  }

  .pn > header {
    flex-shrink: 0;
    height: 38px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 12px;
    border-bottom: 1px solid var(--line);
    background: var(--head);
  }

  .pn > header h2 {
    margin: 0;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.1px;
    color: var(--t2);
  }

  .pn > header .right {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .pn .in {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex-grow: 1;
    min-height: 0;
  }

  .balance {
    grid-area: balance;
  }
  .curve {
    grid-area: curve;
  }
  .perf {
    grid-area: perf;
  }
  .hours {
    grid-area: hours;
  }
  .analysis {
    grid-area: analysis;
  }
  .entry {
    grid-area: entry;
  }
  .lists {
    grid-area: lists;
  }

  /* ---------- the big figure, and the small ones ---------- */

  .big {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .big .fig {
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    font-size: 30px;
    font-weight: 600;
    letter-spacing: -1.1px;
    line-height: 1;
    color: var(--t1);
  }

  .big .sub {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 11.5px;
    color: var(--t4);
  }

  .tiles {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1px;
    background: var(--line-soft, rgba(255, 255, 255, 0.05));
    border: 1px solid var(--line);
    border-radius: 7px;
    overflow: hidden;
  }

  .tiles.three {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  /* Six readings sit three across; on a narrow screen they fall to two. */
  .analysis .tiles {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @container pulse (max-width: 1040px) {
    .analysis .tiles {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .tl {
    background: var(--head);
    padding: 9px 10px 10px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
  }

  .tl > i {
    font-style: normal;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--t4);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tl > b {
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    font-size: 14.5px;
    font-weight: 600;
    letter-spacing: -0.4px;
    color: var(--t1);
  }

  .tl > span {
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 10.5px;
    color: var(--t4);
  }

  .good {
    color: var(--up) !important;
  }
  .bad {
    color: var(--down) !important;
  }

  /* ---------- the realised curve ---------- */

  .plot {
    position: relative;
    flex-grow: 1;
    min-height: 168px;
  }

  .plot svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
  }

  .plot .zero {
    stroke: var(--line);
    stroke-width: 1;
    stroke-dasharray: 2 4;
  }

  .plot .ln {
    fill: none;
    stroke-width: 1.8;
    stroke-linejoin: round;
    stroke-linecap: round;
  }

  .plot .ar {
    stroke: none;
  }

  .plot .empty {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    text-align: center;
    font-size: 11px;
    line-height: 17px;
    color: var(--t4);
    padding: 0 24px;
  }

  .days {
    flex-shrink: 0;
    display: flex;
    justify-content: space-between;
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 9.5px;
    color: var(--t5);
  }

  /* ---------- buckets ---------- */

  .bk {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  .bk .rw {
    display: grid;
    grid-template-columns: 78px minmax(0, 1fr) 42px;
    align-items: center;
    gap: 9px;
  }

  .bk .nm {
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 10px;
    color: var(--t3);
    white-space: nowrap;
  }

  .bk .tr {
    height: 6px;
    border-radius: 3px;
    background: var(--chip);
    overflow: hidden;
  }

  .bk .fl {
    height: 100%;
    border-radius: 3px;
    min-width: 0;
  }

  .bk .ct {
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    font-size: 10.5px;
    text-align: right;
    color: var(--t2);
  }

  .stack {
    display: flex;
    height: 7px;
    border-radius: 4px;
    overflow: hidden;
    background: var(--chip);
  }

  .stack > span {
    height: 100%;
  }

  /* ---------- the week of hours ---------- */

  .heat {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr);
    gap: 6px;
    align-items: center;
  }

  .heat .rows {
    display: grid;
    grid-template-rows: repeat(7, 1fr);
    gap: 3px;
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 9px;
    color: var(--t5);
  }

  .heat .rows span {
    display: flex;
    align-items: center;
    height: 14px;
  }

  .heat .cells {
    display: grid;
    grid-template-rows: repeat(7, 1fr);
    gap: 3px;
  }

  .heat .wk {
    display: grid;
    grid-template-columns: repeat(24, minmax(0, 1fr));
    gap: 3px;
  }

  .heat .cl {
    height: 14px;
    border-radius: 3px;
    background: var(--chip);
  }

  .heat .cl[data-on='1'] {
    cursor: help;
  }

  .hoursfoot {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 10.5px;
    color: var(--t4);
  }

  .scale {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 9px;
    color: var(--t5);
  }

  .scale i {
    width: 12px;
    height: 8px;
    border-radius: 2px;
  }

  .marks {
    display: grid;
    grid-template-columns: repeat(24, minmax(0, 1fr));
    gap: 3px;
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 9px;
    color: var(--t5);
  }

  .marks span {
    text-align: center;
  }

  /* ---------- tables ---------- */

  .tb2 {
    width: 100%;
    border-collapse: collapse;
  }

  .tb2 thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--head);
    height: 28px;
    padding: 0 10px;
    text-align: right;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--t4);
    border-bottom: 1px solid var(--line);
    white-space: nowrap;
  }

  .tb2 thead th:first-child {
    text-align: left;
  }

  .tb2 tbody td {
    height: 44px;
    padding: 0 10px;
    text-align: right;
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    font-size: 11.5px;
    color: var(--t2);
    border-bottom: 1px solid var(--hair);
    white-space: nowrap;
  }

  .tb2 tbody td:first-child {
    text-align: left;
    font-family: inherit;
  }

  .tb2 tbody tr:hover td {
    background: rgba(255, 255, 255, 0.017);
  }

  .tok {
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 0;
  }

  .tok .txt {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .tok .tk {
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: -0.2px;
    color: var(--t1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tok .nm2 {
    font-size: 10.5px;
    color: var(--t4);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
  }

  .tags {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .tag {
    height: 15px;
    padding: 0 5px;
    display: inline-flex;
    align-items: center;
    border-radius: 3px;
    background: var(--chip);
    color: var(--t4);
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
  }

  .scroll {
    flex-grow: 1;
    min-height: 0;
    max-height: 520px;
    overflow: auto;
    scrollbar-width: none;
  }

  .scroll::-webkit-scrollbar {
    display: none;
  }

  /* ---------- wallets ---------- */

  .wcards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 10px;
  }

  .wc {
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 9px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 11px;
    min-width: 0;
  }

  .wc .top2 {
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 0;
  }

  .wc .swatch {
    width: 26px;
    height: 26px;
    flex-shrink: 0;
    border-radius: 7px;
    display: grid;
    place-items: center;
    font-size: 11px;
    font-weight: 700;
    color: #07080b;
  }

  .wc .who {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .wc .who b {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: -0.2px;
    color: var(--t1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .wc .who span {
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 10px;
    color: var(--t4);
  }

  .wc .amt {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }

  .wc .amt b {
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    font-size: 19px;
    font-weight: 600;
    letter-spacing: -0.7px;
    color: var(--t1);
  }

  .wc .amt span {
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 11px;
    color: var(--t4);
  }

  .wc .acts {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: auto;
  }

  .wc .share {
    height: 4px;
    border-radius: 2px;
    background: var(--chip);
    overflow: hidden;
  }

  .wc .share i {
    display: block;
    height: 100%;
    border-radius: 2px;
    background: var(--accent);
  }

  .wtop {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 14px 2px 12px;
  }

  .wtop h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.3px;
    color: var(--t1);
  }

  .wtop .right {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 7px;
  }

  .btn.hot {
    border-color: transparent;
    background: var(--t1);
    color: var(--bg);
  }

  .btn.hot:hover {
    background: #ffffff;
    color: var(--bg);
  }

  /* ---------- shared small controls ---------- */

  .btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 26px;
    padding: 0 9px;
    border-radius: 5px;
    border: 1px solid var(--chip-hi);
    background: var(--chip);
    color: var(--t2);
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
  }

  .btn:hover {
    background: var(--chip-hi);
    color: var(--t1);
  }

  .sel {
    height: 26px;
    padding: 0 7px;
    border-radius: 5px;
    border: 1px solid var(--chip-hi);
    background: var(--chip);
    color: var(--t2);
    font-family: inherit;
    font-size: 11.5px;
    font-weight: 500;
    cursor: pointer;
    max-width: 190px;
  }

  .hint {
    margin: 0;
    font-size: 10.5px;
    line-height: 16px;
    color: var(--t5);
  }

  .blank {
    margin: 0;
    display: grid;
    place-items: center;
    min-height: 120px;
    padding: 24px;
    text-align: center;
    font-size: 11px;
    line-height: 17px;
    color: var(--t4);
  }
`
